import React from 'react';
import { useNavigate } from 'react-router-dom';
import { I } from '../icons-ds';
import {
  Avatar, Card, FilterChip, Row, SectionHeader, Segmented, Tag, Toggle,
} from '../ui';
import type { FilterChipKind } from '../ui';
import { MobileNav, useViewport } from '../layout';
import {
  useTheme, type Theme, type Accent, type Density, type Palette, type Layout,
  type DefaultFolder,
} from '../lib/ThemeContext';
import { usePhrases } from '../lib/PhrasesContext';

const USER_NAME = 'Samuel Edusa, MD';
const USER_EMAIL = 'samuel.edusa@gmail.com';

const ACCENT_SWATCHES: { value: Accent; color: string; label: string }[] = [
  // Auto = the active palette's own accent; its swatch color is resolved at
  // render time from PALETTE_OPTIONS so it previews what Auto would apply.
  { value: 'auto', color: '', label: 'Auto' },
  { value: 'blue', color: '#0A84FF', label: 'Blue' },
  { value: 'amber', color: '#E08E12', label: 'Amber' },
  { value: 'teal', color: 'oklch(0.5756 0.0658 199.4874)', label: 'Teal' },
  { value: 'indigo', color: '#5856D6', label: 'Indigo' },
  { value: 'purple', color: '#8E5BE8', label: 'Purple' },
  { value: 'graphite', color: '#1C1C1E', label: 'Graphite' },
];

// Default-folder choices. Mirror the Search filter chips (kind drives the color);
// 'all' means no default, so Search opens unscoped.
const FOLDER_PREFS: { value: DefaultFolder; label: string; kind: FilterChipKind }[] = [
  { value: 'all', label: 'None', kind: 'neutral' },
  { value: 'inpatient', label: 'Inpatient', kind: 'inpatient' },
  { value: 'outpatient', label: 'Outpatient', kind: 'outpatient' },
  { value: 'icu', label: 'ICU', kind: 'icu' },
  { value: 'ed', label: 'ED', kind: 'ed' },
  { value: 'exam', label: 'Examination', kind: 'exam' },
  { value: 'protocols', label: 'Protocols', kind: 'protocols' },
  { value: 'references', label: 'References', kind: 'references' },
];

// Preview swatches use each palette's light-mode canvas + accent.
const PALETTE_OPTIONS: { value: Palette; label: string; canvas: string; accent: string }[] = [
  { value: 'sandstone', label: 'Sandstone', canvas: 'oklch(0.9555 0.0555 96.1549)', accent: 'oklch(0.5756 0.0658 199.4874)' },
  { value: 'caffeine', label: 'Caffeine', canvas: 'oklch(0.9821 0 0)', accent: 'oklch(0.4341 0.0392 41.9938)' },
  { value: 'elegant-luxury', label: 'Elegant Luxury', canvas: 'oklch(0.9779 0.0042 56.3756)', accent: 'oklch(0.4650 0.1470 24.9381)' },
  { value: 'claude', label: 'Claude', canvas: 'oklch(0.9818 0.0054 95.0986)', accent: 'oklch(0.6171 0.1375 39.0427)' },
  { value: 'amber-minimal', label: 'Amber Minimal', canvas: 'oklch(0.9846 0.0017 247.8389)', accent: 'oklch(0.7686 0.1647 70.0804)' },
  { value: 'nature', label: 'Nature', canvas: 'oklch(0.9711 0.0074 80.7211)', accent: 'oklch(0.5234 0.1347 144.1672)' },
  { value: 'lawsofux', label: 'Laws of UX', canvas: 'hsl(60, 10%, 82%)', accent: 'hsl(200, 50%, 40%)' },
];

export function SettingsScreen() {
  const viewport = useViewport();
  const navigate = useNavigate();
  const isMobile = viewport === 'mobile';

  const {
    theme, setTheme, accent, setAccent, density, setDensity, palette, setPalette, layout, setLayout,
    defaultFolder, setDefaultFolder,
  } = useTheme();
  const { phrases, folders } = usePhrases();

  const signOut = () => {
    try { window.localStorage.removeItem('isAuthenticated'); } catch (e) { /* ignore */ }
    navigate('/login');
  };

  const themeOptions = [
    { value: 'light' as Theme, icon: <I.sun size={14} />, label: 'Light' },
    { value: 'dark' as Theme, icon: <I.moon size={14} />, label: 'Dark' },
    { value: 'system' as Theme, icon: <I.monitor size={14} />, label: 'System' },
  ];
  const densityOptions = [
    { value: 'spacious' as Density, label: 'Spacious' },
    { value: 'balanced' as Density, label: 'Balanced' },
    { value: 'compact' as Density, label: 'Compact' },
  ];
  const layoutOptions = [
    { value: 'grid' as Layout, icon: <I.grid size={14} />, label: 'Grid' },
    { value: 'list' as Layout, icon: <I.list size={14} />, label: 'List' },
  ];

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-grouped)' }}>
      {isMobile && <MobileNav title="Settings" onBack={() => navigate(-1)} backTitle="Back" />}

      <div style={{ maxWidth: 680, margin: '0 auto', padding: isMobile ? '0 16px 32px' : '32px 32px 48px' }}>
        {!isMobile && (
          <div style={{ marginBottom: 28 }}>
            <div className="t-largetitle" style={{ marginBottom: 4 }}>Settings</div>
            <div className="t-subhead" style={{ color: 'var(--fg-secondary)' }}>
              Personalize how DotPhrase looks and feels.
            </div>
          </div>
        )}

        {/* Account card */}
        <Card padded={false} style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16 }}>
            <Avatar name={USER_NAME} size={56} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.3px' }}>{USER_NAME}</div>
              <div className="t-footnote" style={{ color: 'var(--fg-secondary)' }}>{USER_EMAIL}</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Tag kind="accent">Pro</Tag>
                <Tag kind="neutral">{folders.length} folders · {phrases.length} phrases</Tag>
              </div>
            </div>
            <I.chevR size={18} style={{ color: 'var(--fg-quaternary)' }} />
          </div>
        </Card>

        {/* Appearance */}
        <SectionHeader>Appearance</SectionHeader>
        <Card padded={false} style={{ marginBottom: 24, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line-divider)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>Theme</span>
              <Segmented<Theme> value={theme} onChange={setTheme} options={themeOptions} />
            </div>
          </div>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line-divider)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>Layout</span>
              <Segmented<Layout> value={layout} onChange={setLayout} options={layoutOptions} />
            </div>
          </div>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line-divider)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>Density</span>
              <Segmented<Density> value={density} onChange={setDensity} options={densityOptions} />
            </div>
          </div>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line-divider)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>Palette</span>
              <span className="t-footnote" style={{ color: 'var(--fg-tertiary)' }}>
                {PALETTE_OPTIONS.find((p) => p.value === palette)?.label}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {PALETTE_OPTIONS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPalette(p.value)}
                  aria-label={p.label}
                  aria-pressed={p.value === palette}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    background: 'transparent', border: 0, padding: 0, cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      position: 'relative', width: 56, height: 42, borderRadius: 10,
                      background: p.canvas, overflow: 'hidden',
                      border: p.value === palette
                        ? '2px solid var(--accent)'
                        : '2px solid var(--line-border)',
                      boxShadow: p.value === palette ? 'var(--shadow-2)' : 'var(--shadow-1)',
                      transition: 'all var(--dur-fast) var(--ease-out)',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute', right: 8, bottom: 8, width: 18, height: 18,
                        borderRadius: 999, background: p.accent,
                      }}
                    />
                  </span>
                  <span
                    className="t-caption1"
                    style={{
                      color: p.value === palette ? 'var(--fg-primary)' : 'var(--fg-tertiary)',
                      fontWeight: p.value === palette ? 600 : 400,
                    }}
                  >
                    {p.label}
                  </span>
                </button>
              ))}
            </div>
            <div className="t-caption1" style={{ color: 'var(--fg-tertiary)', marginTop: 12 }}>
              Each palette sets its own surfaces and a default accent. Pick an accent below to override it — Auto keeps the palette's own.
            </div>
          </div>
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>Accent color</span>
              <span className="t-footnote" style={{ color: 'var(--fg-tertiary)', textTransform: 'capitalize' }}>{accent}</span>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {ACCENT_SWATCHES.map((a) => {
                const color = a.value === 'auto'
                  ? (PALETTE_OPTIONS.find((p) => p.value === palette)?.accent ?? 'var(--accent)')
                  : a.color;
                return (
                  <button
                    key={a.value}
                    onClick={() => setAccent(a.value)}
                    aria-label={a.label}
                    aria-pressed={a.value === accent}
                    style={{
                      width: 36, height: 36, borderRadius: 999,
                      background: color, cursor: 'pointer',
                      border: a.value === accent ? '3px solid var(--bg-surface)' : '3px solid transparent',
                      boxShadow: a.value === accent ? `0 0 0 2px ${color}` : 'var(--shadow-1)',
                      transition: 'all var(--dur-fast) var(--ease-out)',
                    }}
                  />
                );
              })}
            </div>
          </div>
        </Card>

        {/* Library */}
        <SectionHeader>Library</SectionHeader>
        <Card padded={false} style={{ marginBottom: 24 }}>
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>Default folder</span>
              <span className="t-footnote" style={{ color: 'var(--fg-tertiary)' }}>
                {FOLDER_PREFS.find((f) => f.value === defaultFolder)?.label}
              </span>
            </div>
            <div className="t-caption1" style={{ color: 'var(--fg-tertiary)', marginBottom: 14 }}>
              Search opens scoped to this folder. Choose None to start with all phrases.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {FOLDER_PREFS.map((f) => (
                <FilterChip
                  key={f.value}
                  kind={f.kind}
                  active={f.value === defaultFolder}
                  icon={f.value === defaultFolder ? <I.check size={14} stroke={2.4} /> : undefined}
                  onClick={() => setDefaultFolder(f.value)}
                >
                  {f.label}
                </FilterChip>
              ))}
            </div>
          </div>
        </Card>

        {/* Behavior */}
        <SectionHeader>Behavior</SectionHeader>
        <Card padded={false} style={{ marginBottom: 24 }}>
          <Row
            leading={<I.copy size={18} />}
            title="Always copy as plain text"
            subtitle="Strips all formatting before clipboard write"
            trailing={<Toggle checked disabled onChange={() => {}} />}
            last
          />
        </Card>

        {/* Privacy & Data */}
        <SectionHeader>Privacy &amp; Data</SectionHeader>
        <Card padded={false} style={{ marginBottom: 24 }}>
          <Row
            leading={<I.lock size={18} />}
            title="Phrases stay in your tenant"
            subtitle="No phrase content leaves your account"
            trailing={<I.info size={16} />}
            last
          />
        </Card>

        {/* Account */}
        <SectionHeader>Account</SectionHeader>
        <Card padded={false}>
          <Row leading={<I.arrowR size={18} />} title="Sign out" danger onClick={signOut} last />
        </Card>

        <div className="t-caption1" style={{ textAlign: 'center', color: 'var(--fg-tertiary)', marginTop: 28 }}>
          DotPhrase Search · v2.0 · Maintained by Samuel Edusa, MD
        </div>
      </div>
    </div>
  );
}

export default SettingsScreen;
